import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChangePasswordService } from 'src/app/services/auth/change-password/change-password.service';
import { LoginService } from 'src/app/services/auth/login/login.service';
import { RegisterService } from 'src/app/services/auth/register/register.service';
import { MyDataService } from 'src/app/services/data/data.service';
import { SnackBarService } from 'src/app/services/snackBar/snack-bar.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [
    trigger('inOut', [
      transition('void => *', [
        style({ opacity: 0 }), // initial styles
        animate(
          '300ms',
          style({ opacity: 1 }) // final style after the transition has finished
        ),
      ]),
      transition('* => void', [
        animate(
          '150ms',
          style({ opacity: 0 }) // we asume the initial style will be always opacity: 1
        ),
      ]),
    ]),
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  public title: string = '';

  public formLogin: FormGroup;
  public formRegistro: FormGroup;
  public formRecuperarPass: FormGroup;
  public formCambiarContrasena: FormGroup;
  public formCodigoActivacion: FormGroup;
  public formNuevaContrasena: FormGroup;
  public errorLogin: string = '';
  public errorRegistro: string = '';
  public errorRecuperarPass: string = '';
  public errorCambiarContrasena: string = '';
  public errorCodigo: string = '';
  public login: boolean = true;
  public registro: boolean = false;
  public recuperarPass: boolean = false;
  public cambiarContrasena: boolean = false;

  public enviarDisabled: boolean = false;

  stepChangePassword: 1 | 2 | 3 | 4 = 1;
  userData: any = null;

  logo: any;
  logo2: any;
  logo3: any;

  validEmail = false;

  constructor(
    private _formBuilder: FormBuilder,
    private loginSvc: LoginService,
    private router: Router,
    private dataSvc: MyDataService,
    private snackBarService: SnackBarService,
    private cambioContraseñaSvc: ChangePasswordService,
    private registerSvc: RegisterService,
    private translocoService: TranslocoService
  ) {
    this.formLogin = this._formBuilder.group({
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(200)],
      ],
      password: ['', [Validators.required, Validators.maxLength(200)]],
    });

    this.formRegistro = this._formBuilder.group({
      nombre: ['', [Validators.required, Validators.maxLength(250)]],
      apellidos: ['', [Validators.required, Validators.maxLength(500)]],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(200)],
      ],
      password: ['', [Validators.required, Validators.maxLength(200)]],
      password2: ['', [Validators.required, Validators.maxLength(200)]],
    });

    this.formRecuperarPass = this._formBuilder.group({
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(200)],
      ],
    });

    this.formCodigoActivacion = this._formBuilder.group({
      codigo: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
    });

    this.formNuevaContrasena = this._formBuilder.group({
      newPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(200)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(200)]],
    });
  }

  ngOnInit(): void {
    const token = this.loginSvc.isUserLoggedIn();
    if (token) {
      this.router.navigate(['/casos']).then(() => {
        window.location.reload();
      });
    }

    const logo = this.dataSvc.getLogo();
    if (logo) {
      this.logo = logo;
    } else {
      this.dataSvc.getLogoObservable().subscribe((value) => {
        this.logo = value;
      });
    }

    const logo2 = this.dataSvc.getLogo2();
    if (logo2) {
      this.logo2 = logo2;
    } else {
      this.dataSvc.getLogo2Observable().subscribe((value) => {
        this.logo2 = value;
      });
    }

    const logo3 = this.dataSvc.getLogo3();
    if (logo3) {
      this.logo3 = logo3;
    } else {
      this.dataSvc.getLogo3Observable().subscribe((value) => {
        this.logo3 = value;
      });
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  showSnackBarError(mensaje: string) {
    this.snackBarService.openSnackBarError(mensaje);
  }

  showSnackBarSuccess(mensaje: string) {
    this.snackBarService.openSnackBarSuccess(mensaje);
  }

  acceder() {
    this.loginSvc
      .login(
        this.formLogin.get('email')?.value,
        this.formLogin.get('password')?.value,
        this.dataSvc.getFkEntityPlatform()
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (v) => {
          this.router.navigate(['/casos']).then(() => {
            window.location.reload();
          });
        },
        error: (e) => {
          console.log(e);
          this.showSnackBarError(
            this.translocoService.translate('login.error.invalidCredentials')
          );
        },
      });
  }

  navigateToRegister() {
    this.router.navigate(['/register']);
  }

  navigateToSinRegister() {
    this.router.navigate(['/acceso-sin-registro']);
  }

  validarEmail(email: string) {
    const emailRegExp: RegExp =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    if (email) {
      if (!emailRegExp.test(email)) {
        return false;
      }
    }

    return true;
  }

  comprobarEmail() {
    if (
      this.formRecuperarPass.get('email')?.value !== '' &&
      this.validarEmail(this.formRecuperarPass.get('email')?.value)
    ) {
      this.registerSvc
        .comprobarEmail(this.formRecuperarPass.get('email')?.value)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (data: any) => {
            this.validEmail = !data;
            if (!this.validEmail) {
              this.showSnackBarError(
                this.translocoService.translate('login.error.emailExists')
              );
            }
          },
          error: (err: any) => {
            console.log(err);
            this.showSnackBarError(
              this.translocoService.translate('login.error.emailCheckFailed')
            );
            this.validEmail = false;
          },
        });
    } else {
      if (this.formRecuperarPass.get('email')?.value) {
        this.showSnackBarError(
          this.translocoService.translate('login.error.invalidEmail')
        );
      }
      this.validEmail = false;
    }
  }

 enviarCorreoCambioContrasena() {
  const email = this.formRecuperarPass.get('email')?.value;
  this.cambioContraseñaSvc
    .sendMailCambioContraseña(email)
    .pipe(takeUntil(this.destroyed$))
    .subscribe({
      next: (v) => {
        this.userData = v;
        this.userData.email = email; // Guardar el email del usuario
        this.stepChangePassword = 2;
        this.formRecuperarPass.get('email')?.setValue('');
      },
      error: (e) => {
        this.showSnackBarError(e);
      },
    });
}

  verificarCodigo() {
    if (this.formCodigoActivacion.valid && this.userData) {
      this.cambioContraseñaSvc
        .comprobarClienteChangePass(
          this.userData.userID,
          this.formCodigoActivacion.get('codigo')?.value
        )
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.stepChangePassword = 3;
            this.errorCodigo = '';
          },
          error: (err) => {
            console.error(err);
            this.errorCodigo = this.translocoService.translate('changePassword.error.invalidCode');
          },
        });
    }
  }

  cambiarPassword() {
    if (this.formNuevaContrasena.valid && this.userData) {
      const newPassword = this.formNuevaContrasena.get('newPassword')?.value;
      const confirmPassword = this.formNuevaContrasena.get('confirmPassword')?.value;
      
      if (newPassword !== confirmPassword) {
        this.errorCodigo = this.translocoService.translate('changePassword.error.passwordMismatch');
        return;
      }

      this.cambioContraseñaSvc
        .changePass(
          this.userData.userID,
          newPassword,
          this.formCodigoActivacion.get('codigo')?.value
        )
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.stepChangePassword = 4;
            this.errorCodigo = '';
          },
          error: (err) => {
            console.error('Error changing password:', err);
            this.errorCodigo = this.translocoService.translate('changePassword.error.invalidCode');
          },
        });
    }
  }

  reenviarCodigoCambioPassword() {
    if (this.userData && this.userData.email) {
      this.cambioContraseñaSvc
        .sendMailCambioContraseña(this.userData.email)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.showSnackBarSuccess(
              this.translocoService.translate('changePassword.successCodeResent')
            );
          },
          error: (err) => {
            console.error(err);
            this.showSnackBarError(
              this.translocoService.translate('changePassword.error.resendCode')
            );
          },
        });
    } else {
      this.showSnackBarError(
        this.translocoService.translate('changePassword.error.noEmail')
      );
    }
  }



  cambiarVista(vista: string = '') {
    switch (vista) {
      case 'recuperar':
        this.recuperarPass = true;
        this.login = false;
        this.stepChangePassword = 1;
        this.userData = null;
        this.errorCodigo = '';
        this.formCodigoActivacion.reset();
        this.formNuevaContrasena.reset();
        break;
      case 'login':
        this.login = true;
        this.stepChangePassword = 1;
        this.recuperarPass = false;
        this.userData = null;
        this.errorCodigo = '';
        this.formCodigoActivacion.reset();
        this.formNuevaContrasena.reset();
        break;
    }
  }

  getCurrentYear() {
    return new Date().getFullYear();
  }
}
